import config from "../config/assessmentConfig.json";

// Map config categories to concise keys for the scoring output
const CATEGORY_KEY_MAP = {
  menu_fluency: "menu",
  training_onboarding: "training",
  service_consistency: "service",
  upselling_pairing: "upsell",
  menu_updates_training: "updates",
  guest_feedback_service_quality: "feedback",
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function scaleLikert1to5(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  return clamp(((n - 1) / 4) * 100, 0, 100);
}

function scaleYesNo(val) {
  if (val === "yes") return 100;
  if (val === "no") return 0;
  return null;
}

function scaleOrdinalNRSO(val, polarity = "positive") {
  const v = String(val || "").toLowerCase();
  const POS = { never: 0, rarely: 25, sometimes: 50, often: 75, always: 100 };
  const NEG = { never: 100, rarely: 75, sometimes: 50, often: 25, always: 0 };
  const map = polarity === "negative" ? NEG : POS;
  return map[v] ?? null;
}

// removed legacy scaleNumberDays; handled by linear scaler

function scaleLinear(val, min = 0, max = 100, polarity = "positive") {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  if (max === min) return 0;
  const x = clamp(n, min, max);
  const ratio = (x - min) / (max - min);
  const pct = polarity === "negative" ? (1 - ratio) * 100 : ratio * 100;
  return clamp(pct, 0, 100);
}

// Backwards compatibility helpers
const scaleNumberDays = (val, min, max, polarity) => scaleLinear(val, min ?? 0, max ?? 30, polarity);
const scaleNumberTimesPerYear = (val, min, max, polarity) => scaleLinear(val, min ?? 0, max ?? 12, polarity);

function scaleMcqSingle(val, options = []) {
  if (val == null || val === "") return null;
  // If options are objects with ids and optional score, match by id
  const hasObjectOptions = options.some((o) => typeof o === "object");
  if (hasObjectOptions) {
    const match = options.find((o) => String(o.id) === String(val));
    if (!match) return null;
    if (typeof match.score === "number") return clamp(match.score, 0, 100);
    // fallback if no explicit score
    return 100;
  }
  // String options fallback
  const v = String(val).toLowerCase();
  if (v === "none") return 0;
  if (options.length && !options.map((o) => String(o)).includes(String(val))) return null;
  return 100;
}

function scaleMcqMulti(val, options = []) {
  if (!Array.isArray(val)) return null;
  if (val.length === 0) return null;
  const lower = val.map((x) => String(x).toLowerCase());
  if (lower.includes("none")) return 0;
  // Score by coverage: proportion of non-none options selected
  const validOpts = (options || []).filter((o) => String(o).toLowerCase() !== "none");
  if (validOpts.length === 0) return 100;
  const count = lower.filter((x) => validOpts.map((o) => String(o).toLowerCase()).includes(x)).length;
  return clamp((count / validOpts.length) * 100, 0, 100);
}

// (removed explicit function; handled by linear scaler)

// Build question metadata: id -> { type, categoryKey, options?, min?, max?, polarity? }
const QUESTION_META = (() => {
  const map = {};
  for (const cat of config.categories || []) {
    const catKey = CATEGORY_KEY_MAP[cat.id] || cat.id;
    for (const q of cat.questions || []) {
      map[q.id] = {
        type: q.type,
        categoryKey: catKey,
        options: q.options,
        min: q.min,
        max: q.max,
        polarity: q.polarity, // "positive" | "negative" (optional)
      };
    }
  }
  return map;
})();

function scaleByType(type, value, meta) {
  switch (type) {
    case "likert_1_5":
      return scaleLikert1to5(value);
    case "yes_no":
      return scaleYesNo(value);
    case "ordinal_n_r_s_o":
      return scaleOrdinalNRSO(value, meta?.polarity || "positive");
    case "number_days":
      return scaleNumberDays(value, meta?.min, meta?.max, meta?.polarity || "negative");
    case "mcq_single":
      return scaleMcqSingle(value, meta?.options || []);
    case "mcq_multi":
      return scaleMcqMulti(value, meta?.options || []);
    case "number_times_per_year":
      return scaleNumberTimesPerYear(value, meta?.min, meta?.max, meta?.polarity || "positive");
    case "percent_bracket": {
      // value expected to be option id; use matching option.score
      const opts = meta?.options || [];
      const match = opts.find((o) => String((o.id ?? o)) === String(value));
      if (!match) return null;
      const score = typeof match === "object" ? match.score : undefined;
      return typeof score === "number" ? clamp(score, 0, 100) : null;
    }
    case "hours_bracket": {
      const opts = meta?.options || [];
      const match = opts.find((o) => String((o.id ?? o)) === String(value));
      if (!match) return null;
      const score = typeof match === "object" ? match.score : undefined;
      return typeof score === "number" ? clamp(score, 0, 100) : null;
    }
    default:
      // Default: non-empty answers score 50, empty -> null (ignored)
      return value != null && value !== "" ? 50 : null;
  }
}

function average(values) {
  const nums = values.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (nums.length === 0) return 0;
  const sum = nums.reduce((a, b) => a + b, 0);
  return sum / nums.length;
}

export function scoreAnswers(answers) {
  const buckets = {};
  // Initialize buckets for all categories present in config
  for (const cat of config.categories || []) {
    const catKey = CATEGORY_KEY_MAP[cat.id] || cat.id;
    buckets[catKey] = [];
  }

  for (const [qid, raw] of Object.entries(answers || {})) {
    const meta = QUESTION_META[qid];
    if (!meta) continue;
    const score = scaleByType(meta.type, raw, meta);
    if (typeof score === "number") {
      buckets[meta.categoryKey].push(clamp(score, 0, 100));
    }
  }

  const categories = {};
  for (const [key, arr] of Object.entries(buckets)) {
    categories[key] = Math.round(average(arr));
  }

  const overall = (() => {
    const vals = Object.values(categories);
    if (vals.length === 0) return 0;
    const sum = vals.reduce((a, b) => a + b, 0);
    return Math.round((sum / vals.length) * 10) / 10; // one decimal
  })();

  return { categories, overall };
}
